import { Config360 } from "../components/View360";
import { CuratedConfig } from "../components/ViewCurated";
import { ConfigSlots, parseConfigCommand } from "./configCommands";
import { CsvJsonRow } from "./csvToJson";
import { getAvailableMenuData, MenuData } from "./menuDataFromCsv";

const renderVariantOrder = [
  "CarModel",
  "Environment",
  "ExteriorColour",
  "InteriorColour",
  "LiveryOption",
  "LiveryColour",
  "CarbonColour",
] as const;

const exteriorFrameCount = 36;
const exteriorFrameStep = 1;
const interiorAngles = ["01", "02"];

type RenderVariantName = (typeof renderVariantOrder)[number];

type RenderVariantSlots = Partial<Record<RenderVariantName, string>>;

const emptyRenderVariantSlots = renderVariantOrder.reduce<RenderVariantSlots>(
  (slots, variantName) => ({
    ...slots,
    [variantName]: "00",
  }),
  {},
);

function getCsvDisplayValue(row: CsvJsonRow) {
  return row["Text Override"] || row.Command;
}

function getRendersWith(row: CsvJsonRow) {
  return (row["Renders With"] || "")
    .split(";")
    .map((variantName) => variantName.trim())
    .filter((variantName): variantName is RenderVariantName =>
      renderVariantOrder.includes(variantName as RenderVariantName),
    );
}

function isRenderLayer(row: CsvJsonRow) {
  return row["Not A Layer"]?.trim().toUpperCase() !== "TRUE";
}

function parseLayerOrder(row: CsvJsonRow) {
  const value = row["Layer Order"]?.trim();

  if (!value || value === "-") {
    return undefined;
  }

  const layerOrder = Number(value);
  return Number.isFinite(layerOrder) ? layerOrder : undefined;
}

function getRowsInLayerOrder(rows: CsvJsonRow[]) {
  const hasLayerOrderColumn = rows.some((row) => "Layer Order" in row);

  if (!hasLayerOrderColumn) {
    return rows;
  }

  const categoryOrder = new Map<string, number>();

  for (const row of rows) {
    const category = row.Category || "";

    if (!categoryOrder.has(category)) {
      categoryOrder.set(category, categoryOrder.size);
    }
  }

  return rows
    .map((row, index) => ({
      row,
      index,
      order:
        parseLayerOrder(row) ?? categoryOrder.get(row.Category || "") ?? index,
    }))
    .sort((a, b) => a.order - b.order || a.index - b.index)
    .map(({ row }) => row);
}

function buildRenderPath(slots: RenderVariantSlots) {
  return buildRenderPathForFolder("All_Opt_New", slots);
}

function buildRenderPathForFolder(
  folderName: string,
  slots: RenderVariantSlots,
) {
  const filenamePrefix = renderVariantOrder
    .map((variantName) => slots[variantName] || "00")
    .join(".");

  return `/assets/New Renders/${folderName}/${filenamePrefix}_Frame_{frame}.webp`;
}

export function composeExteriorRenderOptions(
  rows: CsvJsonRow[],
  renderSlots: ConfigSlots,
) {
  return getRowsInLayerOrder(rows).reduce<
    Record<string, Record<string, string>>
  >((options, row) => {
    const command = parseConfigCommand(row.Command);

    if (!command || !isRenderLayer(row)) {
      return options;
    }

    const optionSlots: RenderVariantSlots = {
      ...emptyRenderVariantSlots,
      [command.slot]: command.value,
    };

    for (const dependency of getRendersWith(row)) {
      optionSlots[dependency] = renderSlots[dependency] || "00";
    }

    return {
      ...options,
      [command.slot]: {
        ...options[command.slot],
        [getCsvDisplayValue(row)]: buildRenderPath(optionSlots),
      },
    };
  }, {});
}

export function composeRenderOptions(
  rows: CsvJsonRow[],
  renderSlots: ConfigSlots,
  folderName: string,
) {
  return getRowsInLayerOrder(rows).reduce<
    Record<string, Record<string, string>>
  >((options, row) => {
    const command = parseConfigCommand(row.Command);

    if (!command || !isRenderLayer(row)) {
      return options;
    }

    const optionSlots: RenderVariantSlots = {
      ...emptyRenderVariantSlots,
      [command.slot]: command.value,
    };

    for (const dependency of getRendersWith(row)) {
      optionSlots[dependency] = renderSlots[dependency] || "00";
    }

    return {
      ...options,
      [command.slot]: {
        ...options[command.slot],
        [getCsvDisplayValue(row)]: buildRenderPathForFolder(
          folderName,
          optionSlots,
        ),
      },
    };
  }, {});
}

export function getSelectedExteriorRenderOptions(
  rows: CsvJsonRow[],
  renderSlots: ConfigSlots,
) {
  return rows.reduce<Record<string, string>>((selectedOptions, row) => {
    const command = parseConfigCommand(row.Command);

    if (!command || renderSlots[command.slot] !== command.value) {
      return selectedOptions;
    }

    return {
      ...selectedOptions,
      [command.slot]: getCsvDisplayValue(row),
    };
  }, {});
}

function getFirstOptionSlots(rows: CsvJsonRow[]) {
  return rows.reduce<ConfigSlots>((slots, row) => {
    const command = parseConfigCommand(row.Command);

    if (!command || slots[command.slot]) {
      return slots;
    }

    return {
      ...slots,
      [command.slot]: command.value,
    };
  }, {});
}

function hasOptionValue(rows: CsvJsonRow[], slot: string, value?: string) {
  if (!value) {
    return false;
  }

  return rows.some((row) => {
    const command = parseConfigCommand(row.Command);
    return command?.slot === slot && command.value === value;
  });
}

function getEffectiveRenderSlots(rows: CsvJsonRow[], renderSlots: ConfigSlots) {
  const firstOptionSlots = getFirstOptionSlots(rows);

  return Object.entries(firstOptionSlots).reduce<ConfigSlots>(
    (slots, [slot, firstValue]) => ({
      ...slots,
      [slot]: hasOptionValue(rows, slot, renderSlots[slot])
        ? renderSlots[slot]
        : firstValue,
    }),
    { ...renderSlots },
  );
}

function getMenuRows(menuData: MenuData) {
  return menuData.categories.flatMap((category) =>
    category.subCategories.flatMap((subCategory) =>
      subCategory.options.map((option) => option.raw),
    ),
  );
}

export function buildExterior360Config(
  menuData: MenuData,
  renderSlots: ConfigSlots,
): Config360 {
  const rows = getMenuRows(menuData);
  const initialRenderSlots = getEffectiveRenderSlots(rows, renderSlots);
  const availableRows = getMenuRows(
    getAvailableMenuData(menuData, initialRenderSlots),
  );
  const effectiveRenderSlots = getEffectiveRenderSlots(
    availableRows,
    initialRenderSlots,
  );

  return {
    frameCount: exteriorFrameCount,
    frameStep: exteriorFrameStep,
    options: composeExteriorRenderOptions(availableRows, effectiveRenderSlots),
    selectedOptions: getSelectedExteriorRenderOptions(
      availableRows,
      effectiveRenderSlots,
    ),
  };
}

export function buildInteriorCuratedConfig(
  menuData: MenuData,
  renderSlots: ConfigSlots,
): CuratedConfig {
  const rows = getMenuRows(menuData);
  const interiorRenderSlots = {
    ...renderSlots,
    Environment: "02",
  };
  const initialRenderSlots = getEffectiveRenderSlots(rows, interiorRenderSlots);
  const availableRows = getMenuRows(
    getAvailableMenuData(menuData, initialRenderSlots),
  );
  const effectiveRenderSlots = {
    ...getEffectiveRenderSlots(availableRows, initialRenderSlots),
    Environment: "02",
  };
  return {
    angles: interiorAngles,
    options: composeRenderOptions(
      availableRows,
      effectiveRenderSlots,
      "All_Interior",
    ),
    selectedOptions: getSelectedExteriorRenderOptions(
      availableRows,
      effectiveRenderSlots,
    ),
  };
}
